import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Input,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react";
import { useForfait } from "../../hooks/useForfait";
import { useContact } from "../../hooks/useContact";
import { useIntervention } from "../../hooks/useIntervention";
import { addDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "firebaseConfig";
import { numberToHour, actionIntervention } from "utils";

// Temps d'attente dans la fenêtre, puis durée maximale de suivi de la livraison
const EMAIL_MODAL_WAIT_MS = 8000;
const EMAIL_WATCH_MAX_MS = 5 * 60 * 1000;
// Interventions dont un email est en cours de livraison (partagé entre les ouvertures de la fenêtre)
const pendingEmails = new Set<string>();


interface ModalComponentProps {
  isOpen: boolean;
  onClose: () => void;
  intervention: InterventionType;
  newdata: (data: InterventionType[]) => void;
}

function ModalEmail(props: ModalComponentProps): JSX.Element {
  const { getForfaitField } = useForfait();
  const { getMainContactByClient, getEmailContactBccByClient } = useContact();
  const { fetch } = useIntervention();
  const { getInterventionsByIdForfait, updateInterventionField } =
    useIntervention();
  const { intervention } = props;
  const [mainContact, setMainContact] = useState<ContactType>();
  const [bccContact, setBccContact] = useState<string[] | null>(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [remarque, setRemarque] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [contactLoaded, setContactLoaded] = useState(false);
  const [contactError, setContactError] = useState(false);
  const toast = useToast();
  const handleChangeRemarque = (event: ChangeEvent<HTMLInputElement>) =>
    setRemarque(event.target.value);
  let plugins = "";
  let sauvegarde = "";
  if (intervention.plugins) {
    plugins = " + plugins";
  }
  if (intervention.sauvegarde) {
    sauvegarde = " + sauvegarde";
  }
  useEffect(() => {
    // Ignore les réponses d'une intervention précédente arrivées après un changement
    let isCurrent = true;
    const fetchContact = async () => {
      // Destinataires remis à zéro : jamais ceux d'un autre client
      setMainContact(undefined);
      setBccContact(null);
      setContactLoaded(false);
      setContactError(false);
      try {
        const currentClient: string = await getForfaitField(
          intervention.idforfait,
          "idclient"
        );
        if (currentClient) {
          // Les deux listes sont chargées avant d'être affichées : pas d'envoi avec des copies incomplètes
          const [mainContact, bccContact] = await Promise.all([
            getMainContactByClient(currentClient),
            getEmailContactBccByClient(currentClient),
          ]);
          if (isCurrent) {
            setMainContact(mainContact);
            setBccContact(bccContact);
          }
        }
      } catch (error) {
        console.error(error);
        if (isCurrent) {
          setContactError(true);
        }
      } finally {
        if (isCurrent) {
          setContactLoaded(true);
        }
      }
    };
    const fetchInterventions = async () => {
      setTimeRemaining("");
      try {
        const fetchedInterventions: InterventionType[] =
          await getInterventionsByIdForfait(intervention.idforfait);
        if (fetchedInterventions) {
          const temps = await getForfaitField(intervention.idforfait, "temps");
          if (temps) {
            const somme = fetchedInterventions.reduce(
              (acc, obj) => acc + obj.temps_passe,
              0
            );
            const time = parseFloat(temps) - somme;
            if (isCurrent) {
              setTimeRemaining(numberToHour(time));
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchContact();
    fetchInterventions();
    return () => {
      isCurrent = false;
    };
  }, [getEmailContactBccByClient, getForfaitField, getInterventionsByIdForfait, getMainContactByClient, intervention]);

  const isAlreadyPending = pendingEmails.has(intervention.id);

  async function handleSubmit() {
    // Évite un double envoi (double-clic ou envoi encore en attente) et un envoi sans destinataire
    if (isSending || !mainContact || pendingEmails.has(intervention.id)) {
      return;
    }
    setIsSending(true);
    try {
      if (mainContact) {
        const values = {
          to: mainContact.email,
          cc: bccContact,
          bcc: "contact@mri-freelance.com",
          template: {
            name: "maintenance",
            data: {
              prenom: mainContact.prenom,
              site_internet: intervention.site_internet,
              action: actionIntervention(intervention),
              cms: intervention.cms,
              plugins: plugins,
              sauvegarde: sauvegarde,
              temps_passe: numberToHour(intervention.temps_passe),
              temps_restant: timeRemaining,
              remarque: remarque,
            },
          },
        };
        const docRef = await addDoc(collection(db, "email"), values);
        const interventionId = intervention.id;
        const recipient = mainContact.email;
        pendingEmails.add(interventionId);
        let waitingInModal = true;
        let unsubscribe = () => {};
        let modalTimeout: ReturnType<typeof setTimeout>;
        let watchTimeout: ReturnType<typeof setTimeout>;

        // Suit la livraison par l'extension Trigger Email, y compris après la fermeture
        // de la fenêtre : l'intervention est marquée envoyée dès que la livraison est confirmée
        const stopWatching = () => {
          unsubscribe();
          clearTimeout(modalTimeout);
          clearTimeout(watchTimeout);
          pendingEmails.delete(interventionId);
        };
        unsubscribe = onSnapshot(
          docRef,
          async (snapshot) => {
            const delivery = snapshot.data()?.delivery;
            if (delivery?.state === "SUCCESS") {
              stopWatching();
              if (waitingInModal) {
                setRemarque("");
                setIsSending(false);
                props.onClose();
              }
              try {
                await updateInterventionField(interventionId, "envoi_email", true);
                toast({
                  title: "Email envoyé",
                  description: `Envoyé à ${recipient}`,
                  status: "success",
                  isClosable: true,
                });
                const newData: InterventionType[] = await fetch();
                props.newdata(newData);
              } catch (error) {
                console.error(error);
                toast({
                  title: "Email envoyé, statut non enregistré",
                  description:
                    "L'email est parti mais l'intervention n'a pas pu être marquée comme envoyée.",
                  status: "warning",
                  isClosable: true,
                });
              }
            } else if (delivery?.state === "ERROR") {
              stopWatching();
              console.error("Échec de l'envoi de l'email :", delivery.error);
              if (waitingInModal) {
                setIsSending(false);
              }
              toast({
                title: "Échec de l'envoi de l'email",
                description: delivery.error,
                status: "error",
                isClosable: true,
              });
            }
          },
          (error) => {
            stopWatching();
            console.error(error);
            setIsSending(false);
          }
        );
        // Au-delà de quelques secondes, la fenêtre se ferme mais le suivi continue
        modalTimeout = setTimeout(() => {
          waitingInModal = false;
          setIsSending(false);
          props.onClose();
          toast({
            title: "Envoi en cours",
            description:
              "L'intervention sera marquée comme envoyée dès que la livraison sera confirmée.",
            status: "info",
            isClosable: true,
          });
        }, EMAIL_MODAL_WAIT_MS);
        watchTimeout = setTimeout(() => {
          stopWatching();
          toast({
            title: "Envoi non confirmé",
            description:
              "La livraison n'a pas été confirmée. Vérifiez la collection email avant de le renvoyer.",
            status: "warning",
            isClosable: true,
            duration: 10000,
          });
        }, EMAIL_WATCH_MAX_MS);
      }
    } catch (error) {
      console.error("Error adding data to Firebase:", error);
      setIsSending(false);
      toast({
        title: "Échec de l'envoi de l'email",
        status: "error",
        isClosable: true,
      });
    }
  }
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Envoi d'Email Intervention</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p>Voulez-vous envoyer cette intervention par email :</p>
          {mainContact && (
            <>
              <div style={{ padding: "20px 10px", margin: "10px 0" }}>
                <p>
                  Bonjour {mainContact.prenom},
                  <br />
                  Je viens d'intervenir sur : {intervention.site_internet}
                </p>
                <p>
                  {actionIntervention(intervention)} {intervention.cms} {plugins} {sauvegarde}
                </p>
                <p>
                  Temps passé : {numberToHour(intervention.temps_passe)}
                  <br />
                  Temps restant : {timeRemaining}
                </p>
                <img
                  src="https://mri-freelance.com/signature.png"
                  alt="Signature Jonas Millet"
                  width="200"
                  height="auto"
                />
                <p>
                  <strong>Jonas Millet</strong>
                  <br />
                  Développement web
                  <br />
                  Sites internet et Applications métier
                  <br />
                  06 16 48 75 17
                </p>
              </div>
            </>
          )}
          {isAlreadyPending && !isSending && (
            <Alert status="info" my={4} borderRadius="md">
              <AlertIcon />
              Un email est déjà en cours d'envoi pour cette intervention.
            </Alert>
          )}
          {contactError && (
            <Alert status="error" my={4} borderRadius="md">
              <AlertIcon />
              Impossible de charger les contacts du client : fermez et
              réessayez.
            </Alert>
          )}
          {contactLoaded && !contactError && !mainContact && (
            <Alert status="warning" my={4} borderRadius="md">
              <AlertIcon />
              Aucun contact principal pour ce client : ajoutez-en un avant
              d'envoyer l'email.
            </Alert>
          )}
          <>
            <Text mb="8px">Remarque:</Text>
            <Input
              value={remarque}
              onChange={handleChangeRemarque}
              placeholder="Remarque"
              size="sm"
            />
          </>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="teal"
            mr={3}
            onClick={() => handleSubmit()}
            isDisabled={isSending || !mainContact || isAlreadyPending}
          >
            Envoyer
            {isSending && <Spinner size="sm" ml="4" />}
          </Button>
          <Button variant="ghost" onClick={props.onClose}>
            Fermer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ModalEmail;
